import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isAdmin } from '../api/client';

interface NavItem {
  labelKey: string;
  path: string;
  keywords: string[];
  icon: string;
  adminOnly?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { labelKey: 'nav.darbai', path: '/analytics', keywords: ['darbai', 'inbox', 'demesys', 'work', 'queue'], icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4' },
  { labelKey: 'nav.documents', path: '/documents', keywords: ['dokumentai', 'scan', 'pdf', 'atpazinimas', 'documents'], icon: 'M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z' },
  { labelKey: 'nav.closeWizard', path: '/uzdarymas', keywords: ['uzdarymas', 'vedlys', 'metinis', 'close', 'wizard'], icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' },
  { labelKey: 'nav.automation', path: '/automatizavimas', keywords: ['automatizavimas', 'rutinos', 'routines', 'automation'], icon: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15' },
  { labelKey: 'nav.bankingCash', path: '/banking', keywords: ['bankas', 'operacijos', 'suderinti', 'banking', 'cash'], icon: 'M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3' },
  { labelKey: 'nav.invoices', path: '/invoices/made', keywords: ['invoices', 'sales', 'made', 'outgoing', 'saskaitos', 'israsytos'], icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
  { labelKey: 'invoices.scanTab', path: '/invoices/scan', keywords: ['skenavimas', 'dokumentai', 'scan', 'pdf', 'atpazinimas', 'documents'], icon: 'M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z' },
  { labelKey: 'nav.purchases', path: '/invoices/received', keywords: ['invoices', 'received', 'purchase', 'incoming', 'saskaitos', 'gautos'], icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
  { labelKey: 'invoices.newInvoice', path: '/invoices/new', keywords: ['create', 'new', 'invoice', 'draft', 'nauja', 'saskaita'], icon: 'M12 4v16m8-8H4' },
  { labelKey: 'nav.journal', path: '/journal', keywords: ['journal', 'entries', 'gl', 'general', 'ledger', 'zurnalas', 'irasai'], icon: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253' },
  { labelKey: 'journal.newEntry', path: '/journal/new', keywords: ['create', 'new', 'journal', 'entry', 'manual', 'naujas', 'irasas'], icon: 'M12 4v16m8-8H4' },
  { labelKey: 'nav.accounts', path: '/accounts', keywords: ['accounts', 'chart', 'coa', 'saskaitos', 'planas'], icon: 'M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z' },
  { labelKey: 'dashboard.trialBalance', path: '/accounts/trial-balance', keywords: ['trial', 'balance', 'turnover', 'bandomasis', 'balansas'], icon: 'M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z' },
  { labelKey: 'nav.contacts', keywords: ['persons', 'contacts', 'customers', 'vendors', 'suppliers', 'asmenys', 'kontaktai'], path: '/persons', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z' },
  { labelKey: 'nav.reports', path: '/reports/vat', keywords: ['vat', 'tax', 'reports', 'pvm', 'mokesiai', 'ataskaitos'], icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
  { labelKey: 'nav.taxCompliance', path: '/reports/saft', keywords: ['saft', 'export', 'vmi', 'audit', 'file', 'eksportas'], icon: 'M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
  { labelKey: 'nav.close', path: '/periods/close', keywords: ['period', 'close', 'closing', 'lock', 'periodo', 'uzdarymas'], icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' },
  { labelKey: 'nav.aiAudit', path: '/ai-audit', keywords: ['ai', 'audit', 'decision', 'log', 'agents', 'exceptions', 'dirbtinis', 'intelektas', 'auditas'], icon: 'M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z', adminOnly: true },
  { labelKey: 'nav.arena', path: '/arena', keywords: ['arena', 'agent', 'agents', 'scenario', 'diagnostics', 'agentu', 'arena'], icon: 'M13 10V3L4 14h7v7l9-11h-7z', adminOnly: true },
  { labelKey: 'nav.settings', path: '/settings', keywords: ['settings', 'config', 'company', 'users', 'nustatymai', 'imone'], icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z' },
  { labelKey: 'layout.workspaces', path: '/workspaces', keywords: ['workspace', 'database', 'manage', 'darbo', 'vietos'], icon: 'M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4', adminOnly: true },
];

function fuzzyMatch(query: string, text: string): boolean {
  const q = query.toLowerCase();
  const t = text.toLowerCase();
  // Direct substring match
  if (t.includes(q)) return true;
  // Fuzzy: check if all chars appear in order
  let qi = 0;
  for (let ti = 0; ti < t.length && qi < q.length; ti++) {
    if (t[ti] === q[qi]) qi++;
  }
  return qi === q.length;
}

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

export default function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Role cannot change within an authenticated session — evaluate once at mount
  const availableItems = useMemo(() => (isAdmin() ? NAV_ITEMS : NAV_ITEMS.filter(i => !i.adminOnly)), []);

  const results = useMemo(() => {
    if (!query.trim()) return availableItems;
    return availableItems.filter(item => {
      const labelMatch = fuzzyMatch(query, t(item.labelKey));
      const keywordMatch = item.keywords.some(kw => fuzzyMatch(query, kw));
      return labelMatch || keywordMatch;
    });
  }, [query, availableItems]);

  // Reset selection when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [results]);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setQuery('');
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const handleSelect = useCallback((item: NavItem) => {
    navigate(item.path);
    onClose();
  }, [navigate, onClose]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (results[selectedIndex]) {
          handleSelect(results[selectedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        onClose();
        break;
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* Palette */}
      <div className="relative w-full max-w-lg bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
        {/* Input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200">
          <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('cmd.placeholder')}
            className="flex-1 text-sm border-none outline-none bg-transparent placeholder:text-gray-400"
          />
          <kbd className="hidden sm:inline-flex items-center px-2 py-0.5 text-[10px] font-mono text-gray-400 bg-gray-100 rounded border border-gray-200">
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div className="max-h-72 overflow-y-auto py-2">
          {results.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-gray-400">
              {t('cmd.noResults')} — &quot;{query}&quot;
            </div>
          ) : (
            results.map((item, index) => (
              <button
                key={item.path + item.labelKey}
                onClick={() => handleSelect(item)}
                onMouseEnter={() => setSelectedIndex(index)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition-colors ${
                  index === selectedIndex
                    ? 'bg-primary-50 text-primary-900'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <svg className={`w-4 h-4 flex-shrink-0 ${index === selectedIndex ? 'text-primary-600' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={item.icon} />
                </svg>
                <span className="font-medium">{t(item.labelKey)}</span>
                <span className="text-xs text-gray-400 ml-auto">{item.path}</span>
              </button>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center gap-4 px-4 py-2 border-t border-gray-100 text-[10px] text-gray-400">
          <span className="flex items-center gap-1">
            <kbd className="px-1 py-0.5 bg-gray-100 rounded border border-gray-200 font-mono">&uarr;&darr;</kbd>
            {t('cmd.navigate')}
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1 py-0.5 bg-gray-100 rounded border border-gray-200 font-mono">Enter</kbd>
            {t('cmd.select')}
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1 py-0.5 bg-gray-100 rounded border border-gray-200 font-mono">Esc</kbd>
            {t('cmd.close')}
          </span>
        </div>
      </div>
    </div>
  );
}

